import { Configuration, FrontendApi } from '@ory/kratos-client';
import type { ReactElement } from 'react';
import type { NonIndexRouteObject } from 'react-router';
import type { Merge } from 'type-fest';

import { LOGIN, type PageTitles } from '@revelio/core';
import { Role, Tab } from '@revelio/data-access';

import { isRoleRevelioAdmin } from './auth.ory.hooks';

export type TabRequirement =
  | Tab
  | Tab[]
  | {
      allOf: Tab[];
    };

export interface NavItemConfig extends Omit<NonIndexRouteObject, 'children'> {
  name: string | PageTitles;
  serverTab: TabRequirement;
  icon?: string | ReactElement;
  admin?: boolean;
  clientAdmin?: boolean;
  parent?: string | PageTitles;
  divider?: boolean;
  isSourcesTab?: boolean;
  excludeFromSideNavButtons?: boolean;
  /**
   * Who reports this route's `page visit`. Absent, the route itself does, with
   * the `name` and an id derived from the `path`. `self` = the page component
   * sends a richer visit of its own (a CustomDash page's id and type, a report
   * id, a per-iframe path); `none` = the route is a redirect, with no page to
   * report. A nav group's value is inherited by its children.
   */
  pageVisit?: 'self' | 'none';
}

export interface RouteNavConfig extends Partial<NavItemConfig> {
  children?: RouteNavConfig[];
  nestedRoutes?: RouteNavConfig[];
}

export enum RouterRoutePaths {
  LANDING = '/',
  OVERVIEW = '/company/compositions',
  TRANSITION = '/company/transitions',
  POSTING = '/company/postings',
  SENTIMENT = '/company/sentiment-ratings',
  SCREENER = '/company/screener',
  TALENT_DISCOVERY = '/talent-discovery',
  RESUME_ENRICHMENT = '/resume-enrichment',
  ENRICHED_RESUME = '/resume-enrichment/:id',
  DATA_DICTIONARY = '/data-dictionary',
  MANAGE = '/manage',
}

export const ServerProfileTabsPathMap: Merge<
  { [key in Tab]?: RouterRoutePaths },
  { landing: RouterRoutePaths }
> = {
  landing: RouterRoutePaths.LANDING,
  [Tab.Overview]: RouterRoutePaths.OVERVIEW,
  [Tab.Transition]: RouterRoutePaths.TRANSITION,
  [Tab.Posting]: RouterRoutePaths.POSTING,
  [Tab.Sentiment]: RouterRoutePaths.SENTIMENT,
  [Tab.Screener]: RouterRoutePaths.SCREENER,
  [Tab.TalentDiscovery]: RouterRoutePaths.TALENT_DISCOVERY,
  [Tab.DataDictionary]: RouterRoutePaths.DATA_DICTIONARY,
};

interface AuthgInitConfig {
  oryKratosRoot?: string;
  dashMetaRoot?: string;
  accountPortalRoot?: string;
  accountPortalBackendRoot?: string;
}

export let DASH_META_ROOT = '';
export let ACCOUNT_PORTAL_ROOT = '';
export let ACCOUNT_PORTAL_BACKEND_ROOT = '';
export let ORY_KRATOS_ROOT = '';
export let oryClient: FrontendApi | null = null;

export function initAuth(config: AuthgInitConfig) {
  DASH_META_ROOT = config.dashMetaRoot || '';
  ACCOUNT_PORTAL_ROOT = config.accountPortalRoot || '';
  ACCOUNT_PORTAL_BACKEND_ROOT = config.accountPortalBackendRoot || '';
  ORY_KRATOS_ROOT = config.oryKratosRoot || '';
  oryClient = new FrontendApi(
    new Configuration({
      basePath: ORY_KRATOS_ROOT,
      baseOptions: {
        withCredentials: true,
      },
    })
  );
  return Promise.resolve(true);
}

const MIN_REQUIRED_TABS_FOR_ENTITY_SUMMARY = [
  Tab.TalentDiscovery,
  Tab.Overview,
  Tab.Posting,
  Tab.Sentiment,
  Tab.Compensation,
];

const hasEntitySummaryTab = (userTabs: Tab[]) =>
  MIN_REQUIRED_TABS_FOR_ENTITY_SUMMARY.every((t) => {
    if (Array.isArray(t)) {
      return t.some((t) => userTabs.includes(t));
    }
    return userTabs.includes(t);
  });

export const isTabAllowed = (
  tab: TabRequirement | typeof LOGIN,
  userTabs: Tab[]
): boolean => {
  if (tab === Tab.EntitySummary) {
    return hasEntitySummaryTab(userTabs);
  }

  if (Array.isArray(tab)) {
    return tab.some((t) => userTabs.includes(t));
  }

  if (typeof tab === 'object') {
    return tab.allOf.every((t) => isTabAllowed(t, userTabs));
  }

  return tab === LOGIN || userTabs.includes(tab);
};

export function filterNavItemsForUser(
  items: RouteNavConfig[],
  userServerTabs: Tab[] = [],
  userRole?: Role | null
) {
  const isAdmin = isRoleRevelioAdmin(userRole);
  const isClientAdmin = userRole === Role.ClientAdmin;
  return items.reduce((result, r) => {
    if (!r.serverTab && !r.children) {
      const adminCheckOk =
        (r.admin && isAdmin) ||
        (r.clientAdmin && (isAdmin || isClientAdmin)) ||
        (!r.admin && !r.clientAdmin);
      const shouldAddItem = r.icon ? adminCheckOk : false;
      if (shouldAddItem) {
        result.push(r);
      }
      return result;
    }

    if (r.children) {
      const filteredChildren = filterNavItemsForUser(
        r.children,
        userServerTabs,
        userRole
      );
      if (filteredChildren.length) {
        result.push({ ...r, children: filteredChildren });
      }
      return result;
    }

    if (r.serverTab && isTabAllowed(r.serverTab, userServerTabs)) {
      if (r.admin) {
        isAdmin && result.push(r);
        return result;
      }
      result.push(r);
      return result;
    }

    return result;
  }, [] as RouteNavConfig[]);
}
